/**
 * guide/exit-card EXIT URL card island — ping POST /api/exit-clicks on "바로 열기".
 *
 * Mount: `<div data-mount="exit-card" data-service-slug="..." data-guide-slug="...">`
 * Trigger: any descendant `<a data-exit-link ...>` click. The browser handles
 *   the external redirect itself (target=_blank); this island only sends the
 *   click ping fire-and-forget so the count lands in `exit_click_daily`.
 *
 * Dedup: server-side, KV `exit-click-dedup:<svc>/<guide>:<ip-hash>` 30 min.
 *   Client does NOT dedup — the user pressing twice in 30 min is the server's
 *   problem to ignore.
 *
 * No anon_id — pageview-class signal (IP hash dedup at the server).
 */

type Pingable = {
  serviceSlug: string;
  guideSlug: string;
};

function readPingable(el: HTMLElement): Pingable | null {
  const serviceSlug = el.dataset.serviceSlug;
  const guideSlug = el.dataset.guideSlug;
  if (!serviceSlug || !guideSlug) return null;
  return { serviceSlug, guideSlug };
}

function sendPing(target: Pingable): void {
  const body = JSON.stringify({
    service_slug: target.serviceSlug,
    guide_slug: target.guideSlug,
  });
  // sendBeacon survives navigation; preferred for fire-and-forget alongside
  // an external redirect.
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon("/api/exit-clicks", blob);
    return;
  }
  // Fallback: keepalive fetch — also survives the navigation that the
  // <a target="_blank"> click triggers.
  void fetch("/api/exit-clicks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    // Silent — this is a best-effort signal, not user-facing.
  });
}

export function mountExitCardPing(root: HTMLElement): void {
  const target = readPingable(root);
  if (!target) {
    // Markup error — bail without binding so a missing data attribute
    // can't generate one error per click forever.
    return;
  }
  for (const link of root.querySelectorAll<HTMLAnchorElement>("a[data-exit-link]")) {
    link.addEventListener("click", () => sendPing(target));
  }
}
